import { useQuery } from '@apollo/client';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import {
  DispensarySortByType,
  GetChainDispensariesDocument,
  GetChainDispensariesQueryVariables,
} from 'src/gql/graphql';
import { DispensaryChainReturn } from './types';

export function useDispensaryChains({
  dispensaryId,
  address,
  deliversToAddress = false,
  activeOnly = false,
  includeStealthMode = false,
  skip = false,
  radiusMiles,
}: {
  dispensaryId?: string;
  address?: any;
  deliversToAddress?: boolean;
  activeOnly?: boolean;
  includeStealthMode?: boolean;
  skip?: boolean;
  /** Overrides the default address-based distance filter with an explicit radius, in miles. */
  radiusMiles?: number;
}): DispensaryChainReturn {
  // Get the source dispensary, passing the explicit ID if provided
  const { dispensary } = useDispensary(dispensaryId);

  // The server's `distance` filter is itself in miles (it multiplies by meters-per-mile
  // internally before running the geo query) -- pass radiusMiles straight through.
  let distance: number | null = null;
  if (radiusMiles) {
    distance = radiusMiles;
  } else if (address) {
    distance = 1000.0;
  }

  const variables: GetChainDispensariesQueryVariables = {
    dispensaryFilter: {
      type: `Dispensary`,
      chain: dispensary?.chain,
      nearLat: address?.lat,
      nearLng: address?.lng,
      destinationTaxState: address?.state,
      destinationTaxZipcode: address?.zipcode,
      distance,
      deliversToAddress,
      activeOnly,
      includeStealthMode,
      sortBy: DispensarySortByType.distance,
    },
  };

  const { data, ...apolloValues } = useQuery(GetChainDispensariesDocument, {
    variables,
    skip: !dispensary?.id || skip,
  });

  return {
    dispensaryChainLocations: data?.filteredDispensaries ?? [],
    ...apolloValues,
  };
}
